import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import MaterialTable from 'material-table';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import OverLoading from '../../global/OverLoading';
import apiService from '../../../services.js/apiService';
import Util, { formatDateToDayMonthNameYear, showDateInSGTime } from '../../../common/util';
import { Colours, Url } from '../../../common/constants';
import { IsEmpty } from '../../../common/checks';
import PreCreateModal from './preCreateModal';

const PageHighlightListing = ({ match, location }) => {
  const tableRef = useRef(null);
  const [pageNo] = useState(1);
  const [keyForReload, setKeyForReload] = useState(false);

  const [loading, setLoading] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [isSpecial, setIsSpecial] = useState(false);
  const [isExclusive, setIsExclusive] = useState(false);
  const [pageId, setPageId] = useState(null);
  const [openPreCreateModal, setOpenPreCreateModal] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  useEffect(() => {
    try {
      const query = Util.ExtractParamsFromUrlQueryStr(location.search);
      if (!IsEmpty(query)) {
        setIsSpecial(query.isSpecial === 'true' || false);
        setIsExclusive(query.isExclusive === 'true' || false);
      }

      setPageId(match.params.id || null);
      setKeyForReload(!keyForReload);
    } catch (error) {
      console.log('error', error);
    }
  }, [location.search]);

  const onNewHighlightBtnClicked = () => {
    setOpenPreCreateModal(true);
  };

  const executeCreatePageHighlight = async ({ title }) => {
    try {
      setLoading(true);
      const pageHighlight = await apiService.createPageHighlight({ pageId, title, isSpecial, isExclusive });

      setOpenPreCreateModal(false);
      setLoading(false);

      // navigate to the pageHighlight site
      let thisRedirectUrl = Url.PageHighlight.replace(':pageId', pageId).replace(':pageHighlightId', pageHighlight.id);
      thisRedirectUrl += isSpecial ? '?isSpecial=true' : isExclusive ? '?isExclusive=true' : '';
      setRedirectUrl(thisRedirectUrl);
    } catch (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await apiService.deletePageHighlight(pageId, idToDelete);
      toast.success('Highlight deleted');
      setIdToDelete(null);

      // refresh table
      tableRef.current.onQueryChange();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return redirectUrl ? <Redirect to={redirectUrl} /> : (
    <>
      {loading && <OverLoading />}
      <div className="row" style={{ backgroundColor: Colours.White }}>
        <div className="col-12 d-flex flex-row align-items-center pl-0">
          <h3>
            {isSpecial ? `Special ` : isExclusive ? `Exclusive ` : ''}
            Highlights
          </h3>

          <Button className="ml-3" variant="contained" color="primary" onClick={onNewHighlightBtnClicked}>
            Add New
          </Button>
        </div>

        <div className="col-12 mt-3 pl-0" key={keyForReload}>
          <MaterialTable
            tableRef={tableRef}
            title=""
            columns={[
              { title: 'ID', field: 'id', width: '5%' },
              { title: 'Title', field: 'title', width: '30%' },
              {
                title:  'Start Date', field:  'startDate', width:  '10%',
                render: (d) => (d.startDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(d.startDate))}` : '-'),
              },
              {
                title:  'End Date', field:  'endDate', width:  '10%',
                render: (d) => (d.endDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(d.endDate))}` : '-'),
              },
              { title: 'Status', field: 'status', width: '5%' },
            ]}
            actions={[
              {
                icon:    'edit',
                tooltip: 'Edit Highlight',
                onClick: (event, d) => {
                  window.open(`/admin/page/${pageId}/highlight/${d.id}${isSpecial ? '?isSpecial=true' : isExclusive ? '?isExclusive=true' : ''}`, '_blank');
                },
              },
              {
                icon:    'delete',
                tooltip: 'Delete Highlight',
                onClick: (event, d) => {
                  setIdToDelete(d.id);
                },
              },
            ]}
            options={{
              pageSize:            25,
              pageSizeOptions:     [25, 50, 80],
              actionsColumnIndex:  -1,
              emptyRowsWhenPaging: false,
              search:              false,
              sorting:             false,
            }}
            data={(query) => new Promise(async (resolve, reject) => {
              const { rows, totalCount } = await apiService.getPageHighlights(
                { pageId, isSpecial, isExclusive, pageNo: pageNo - 1, pageSize: query.pageSize },
              );
              resolve({
                data: rows || [],
                page: pageNo - 1,
                totalCount,
              });
            })}
          />
        </div>
      </div>

      {
        openPreCreateModal
          ? (<PreCreateModal onComplete={executeCreatePageHighlight} onClose={() => setOpenPreCreateModal(false)} />)
          : <div />
      }

      <Modal isOpen={!IsEmpty(idToDelete)} toggle={() => setIdToDelete(null)} autoFocus={false}>
        <ModalHeader toggle={() => setIdToDelete(null)}>Delete Confirmation</ModalHeader>
        <ModalBody>
          <h5>
            Deleting this highlight ( id=
            {idToDelete}
            {' '}
            ) will delete all posts created under it as well.
          </h5>
          <h5>Are you sure?</h5>
        </ModalBody>
        <ModalFooter>
          <Button variant="contained" onClick={() => setIdToDelete(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" className="mx-3" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

PageHighlightListing.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  }).isRequired,

  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

export default connect(null, null)(PageHighlightListing);
